// Verkeersdata voor de Corridors-tab.
//
// WAAROM DIT BESTAND ER ZO UITZIET (gemeten op 2026-09-08, project
// fxlorumfwzrdwiftbalo):
//   - public.laadplein_verkeersdata staat op 0 rijen en heeft er sinds de
//     statistiekreset van 2025-06-11 nooit een gehad (n_tup_ins = 0).
//   - public.laadplein_data_sync_log kent 18 runs OpenChargeMap (2.378 records)
//     en 13 runs rdw (1.620 records). Voor bron 'ndw': nul runs, nul records.
//   - cron.job bevat 'laadplein-ocm-weekly' en 'laadplein-rdw-monthly'; een
//     NDW-job bestaat niet (docs/laadplein/migration-live-data.sql heeft hem
//     nooit gehad, de spec in docs/superpowers/specs/2026-04-03-... wel).
//   - public.laadplein_locaties heeft 1 rij, waarvan 0 met ndw_meetpunt_ids en
//     0 met ndw_last_sync. Er valt dus ook niets te pollen.
//   - supabase/functions/laadplein-ndw-traffic/index.ts geeft in mode
//     'ndw_api' HTTP 501 terug: de NDW Dexter-koppeling is nooit gebouwd en de
//     OAuth-credentials (NDW_CLIENT_ID/NDW_CLIENT_SECRET) zijn er niet.
//
// Daarom valt dit scherm terug op de vaste constante CORRIDORS. Die terugval
// mag niet stil zijn: elke corridor draagt zijn eigen herkomst mee in de
// uitvoer (`herkomst`, `gemeten`, `herkomst_label`), zodat een scherm of een
// export niet kan doen alsof een aanname een meting is. `hasLiveData` blijft
// bestaan voor bestaande aanroepers, maar is nadrukkelijk niet meer het enige
// verschil dat een gebruiker kan zien.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CORRIDORS } from '@/constants/laadplein-data';
import type { Corridor } from '@/types/laadplein';

const QUERY_KEY = ['laadplein_verkeersdata'];

/**
 * Datum waarop de getallen in CORRIDORS zijn vastgelegd.
 * `git log --diff-filter=A -- src/constants/laadplein-data.ts` geeft precies
 * één commit: 5ea9f224, 2026-04-08. De constante is sindsdien niet gewijzigd.
 */
export const AANNAME_VASTGELEGD_OP = '2026-04-08';

/** Waar een getal op de Corridors-tab vandaan komt. */
export type VerkeersHerkomst = 'ndw' | 'handmatig' | 'aanname';

export interface VerkeersRecord {
  id: string;
  weg: string;
  richting: string;
  vrachtwagens_per_dag: number;
  datum: string;
  locatie_id?: string;
  /** Kolom `bron` van laadplein_verkeersdata; DB-default is 'handmatig'. */
  bron?: string | null;
}

/** Een corridor die zijn eigen herkomst meedraagt. */
export interface CorridorMetHerkomst extends Corridor {
  herkomst: VerkeersHerkomst;
  /** true zodra het getal uit een record komt in plaats van uit de constante. */
  gemeten: boolean;
  /** Datum van de meting, of de datum waarop de aanname is vastgelegd. */
  peildatum: string;
  /** Zinsnede die naast het getal getoond kan worden. */
  herkomst_label: string;
}

export interface HerkomstSamenvatting {
  totaal: number;
  gemeten: number;
  aanname: number;
  allesAanname: boolean;
  deelsGemeten: boolean;
  /** Nederlandse zin die het scherm ongewijzigd kan tonen. */
  tekst: string;
}

/** 'YYYY-MM-DD' -> 'DD-MM-YYYY'. Locale-vrij, zodat tests deterministisch zijn. */
export function formatteerDatum(datum: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(datum ?? '');
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (datum ?? '');
}

function sleutel(weg: string, richting: string): string {
  return `${weg}|${richting}`;
}

/**
 * Herkomst van een record. Alles wat niet letterlijk 'ndw' is, is handmatige
 * invoer — de edge function schrijft namelijk `bron: 'handmatig'` en de
 * DB-default is dat ook. Zo kan een met de hand ingetypt getal nooit als
 * NDW-meting op het scherm belanden.
 */
export function herkomstVanRecord(bron: string | null | undefined): VerkeersHerkomst {
  return bron === 'ndw' ? 'ndw' : 'handmatig';
}

/**
 * Nieuwste record per weg+richting. Kiest expliciet op datum in plaats van te
 * vertrouwen op de sorteervolgorde van de query: een gewijzigde `order()` mag
 * niet stilletjes een oude meting bovenaan zetten.
 */
export function nieuwsteMetingPerCorridor(
  records: readonly VerkeersRecord[],
): Map<string, VerkeersRecord> {
  const map = new Map<string, VerkeersRecord>();
  for (const rec of records ?? []) {
    if (!rec || typeof rec.vrachtwagens_per_dag !== 'number') continue;
    const key = sleutel(rec.weg, rec.richting);
    const huidig = map.get(key);
    if (!huidig || String(rec.datum ?? '') > String(huidig.datum ?? '')) {
      map.set(key, rec);
    }
  }
  return map;
}

/**
 * Legt de metingen over de vaste corridors heen en geeft elke corridor zijn
 * herkomst mee. Zonder metingen komt er dus geen kale kopie van CORRIDORS
 * terug, maar twaalf corridors die zichzelf 'aanname' noemen.
 */
export function resolveCorridors(
  basis: readonly Corridor[],
  records: readonly VerkeersRecord[],
): CorridorMetHerkomst[] {
  const metingen = nieuwsteMetingPerCorridor(records);

  return (basis ?? []).map((c) => {
    const meting = metingen.get(sleutel(c.weg, c.richting));

    if (!meting) {
      return {
        ...c,
        herkomst: 'aanname',
        gemeten: false,
        peildatum: AANNAME_VASTGELEGD_OP,
        herkomst_label: `Aanname — vast getal uit de code, ${formatteerDatum(AANNAME_VASTGELEGD_OP)}`,
      };
    }

    const herkomst = herkomstVanRecord(meting.bron);
    return {
      ...c,
      trucks_dag: meting.vrachtwagens_per_dag,
      herkomst,
      gemeten: true,
      peildatum: meting.datum,
      herkomst_label:
        herkomst === 'ndw'
          ? `NDW-meting ${formatteerDatum(meting.datum)}`
          : `Handmatig ingevoerd ${formatteerDatum(meting.datum)}`,
    };
  });
}

/** Telt hoeveel van de getoonde getallen gemeten zijn, en zegt het in woorden. */
export function herkomstSamenvatting(
  corridors: readonly CorridorMetHerkomst[],
): HerkomstSamenvatting {
  const totaal = corridors?.length ?? 0;
  const gemeten = (corridors ?? []).filter((c) => c.gemeten).length;
  const aanname = totaal - gemeten;

  let tekst: string;
  if (totaal === 0) {
    tekst = 'Geen corridors om te tonen.';
  } else if (gemeten === 0) {
    tekst =
      `Niet gemeten: alle ${totaal} getallen op dit scherm zijn vaste aannames uit de code, ` +
      `vastgelegd op ${formatteerDatum(AANNAME_VASTGELEGD_OP)} en sindsdien niet gewijzigd.`;
  } else if (aanname === 0) {
    tekst = `Alle ${totaal} getallen komen uit een meting.`;
  } else {
    tekst =
      `${gemeten} van de ${totaal} getallen komen uit een meting; ` +
      `de overige ${aanname} zijn vaste aannames uit ${formatteerDatum(AANNAME_VASTGELEGD_OP)}.`;
  }

  return {
    totaal,
    gemeten,
    aanname,
    allesAanname: totaal > 0 && gemeten === 0,
    deelsGemeten: gemeten > 0 && aanname > 0,
    tekst,
  };
}

export function useLaadpleinVerkeersdata() {
  const queryClient = useQueryClient();

  const { data: dbRecords = [], isLoading } = useQuery<VerkeersRecord[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('laadplein_verkeersdata')
        .select('*')
        .order('datum', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const corridors = resolveCorridors(CORRIDORS, dbRecords);
  const herkomst = herkomstSamenvatting(corridors);
  const hasLiveData = herkomst.gemeten > 0;

  function isCorridorLive(weg: string, richting: string): boolean {
    return corridors.some((c) => c.weg === weg && c.richting === richting && c.gemeten);
  }

  // Let op: deze mutatie heeft sinds 2026-04-03 geen enkele aanroeper in src/.
  // Ze blijft staan omdat mode 'manual' de enige werkende schrijfweg naar
  // laadplein_verkeersdata is; wie ooit een importscherm bouwt heeft hem nodig.
  // Voor een aanroep is een geldige `locatie_id` nodig: de kolom is NOT NULL
  // met een FK naar laadplein_locaties (1 rij op 2026-09-08).
  const importManual = useMutation({
    mutationFn: async ({ locatie_id, data }: { locatie_id: string; data: unknown }) => {
      const { data: result, error } = await supabase.functions.invoke(
        'laadplein-ndw-traffic',
        { body: { mode: 'manual', locatie_id, data } }
      );
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return { corridors, herkomst, hasLiveData, isCorridorLive, importManual, isLoading };
}
